import React, { useState, useRef, useEffect } from 'react';
import { 
  FiBold, 
  FiItalic, 
  FiUnderline, 
  FiImage,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
  FiList,
  FiTrash2,
  FiMinus,
  FiX
} from 'react-icons/fi';
import { userRequest } from '../../../utils/requestMethods';
import { toast } from 'react-toastify';

const WysiwygEditor = ({ 
  value = '', 
  onChange, 
  placeholder = 'Enter text here...',
  className = '',
  minHeight = 100
}) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imageSize, setImageSize] = useState({ width: '100%', height: 'auto' });
  const [imageAlign, setImageAlign] = useState('left');

  // Update editor content when value prop changes
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      const selection = window.getSelection();
      const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
      editorRef.current.innerHTML = value || '';
      
      // Restore cursor position if possible
      if (range && editorRef.current.contains(range.startContainer)) {
        try {
          selection.removeAllRanges();
          selection.addRange(range);
        } catch (e) {
          // Ignore if range is invalid
        }
      }
    }
  }, [value]);

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleContentChange();
  };

  const handleContentChange = () => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const insertImage = (imageUrl, width = '100%', height = 'auto', align = 'left') => {
    if (!editorRef.current) return;

    editorRef.current.focus();
    const selection = window.getSelection();
    
    const createImageElement = () => {
      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = '';
      img.style.maxWidth = width;
      img.style.height = height;
      img.style.cursor = 'pointer';
      
      // Handle alignment
      if (align === 'center') {
        img.style.display = 'block';
        img.style.margin = '0 auto';
        img.style.float = 'none';
      } else if (align === 'right') {
        img.style.display = 'block';
        img.style.margin = '0 0 0 auto';
        img.style.float = 'none';
      } else {
        img.style.display = 'inline-block';
        img.style.margin = '0';
        img.style.float = 'none';
      }
      
      // Add click handler to edit image
      img.addEventListener('click', () => {
        setUploadedImage(imageUrl);
        setImageSize({ width: img.style.maxWidth || '100%', height: img.style.height || 'auto' });
        setImageAlign(align);
        setShowImageModal(true);
      });
      
      return img;
    };
    
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const img = createImageElement();
      range.insertNode(img);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      const img = createImageElement();
      editorRef.current.appendChild(img);
    }
    
    handleContentChange();
  };

  const handleImageUpload = async (file) => {
    if (!file) return null;

    // Validate file size (max 500KB)
    const maxSize = 500 * 1024;
    if (file.size > maxSize) {
      toast.error('Image size must be less than 500KB');
      return null;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPG, PNG, GIF, or WEBP)');
      return null;
    }

    try {
      const formData = new FormData();
      formData.append('attachments', file);

      const response = await userRequest.post('/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const fileData = Array.isArray(response.data) ? response.data[0] : response.data;
      const imageUrl = fileData?.url;

      if (imageUrl) {
        toast.success('Image uploaded successfully!');
        return imageUrl;
      } else {
        throw new Error('No image URL returned from server');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(error.response?.data?.message || 'Failed to upload image. Please try again.');
      return null;
    }
  };

  const handleImageButtonClick = async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      const imageUrl = await handleImageUpload(file);
      if (imageUrl) {
        insertImage(imageUrl, imageSize.width, imageSize.height, imageAlign);
      }
    };
  };

  const updateImageInEditor = (newWidth, newHeight, newAlign) => {
    if (!editorRef.current || !uploadedImage) return;

    // Find and update the image
    const images = editorRef.current.querySelectorAll('img');
    images.forEach(img => {
      if (img.src === uploadedImage) {
        img.style.maxWidth = newWidth;
        img.style.height = newHeight;
        
        // Handle alignment
        if (newAlign === 'center') {
          img.style.display = 'block';
          img.style.margin = '0 auto';
          img.style.float = 'none';
        } else if (newAlign === 'right') {
          img.style.display = 'block';
          img.style.margin = '0 0 0 auto';
          img.style.float = 'none';
        } else {
          img.style.display = 'inline-block';
          img.style.margin = '0';
          img.style.float = 'none';
        }
      }
    });

    handleContentChange();
    setShowImageModal(false);
    setUploadedImage(null);
  };

  const deleteImageFromEditor = () => {
    if (!editorRef.current || !uploadedImage) return;

    const images = editorRef.current.querySelectorAll('img');
    images.forEach(img => {
      if (img.src === uploadedImage) {
        img.remove();
      }
    });

    handleContentChange();
    setShowImageModal(false);
    setUploadedImage(null);
  };

  const isCommandActive = (command) => {
    try {
      return document.queryCommandState(command);
    } catch (e) {
      return false;
    }
  };

  return (
    <div className={`wysiwyg-editor-wrapper ${className}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 bg-white border border-b-0 border-gray-200 rounded-t-lg">
        {/* Text Formatting */}
        <div className="flex items-center gap-1 pr-2 border-r border-gray-300">
          <button
            type="button"
            onClick={() => execCommand('formatBlock', '<h1>')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isCommandActive('formatBlock') ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700'
            }`}
            title="Heading 1"
          >
            <span className="text-sm font-bold">H1</span>
          </button>
          <button
            type="button"
            onClick={() => execCommand('formatBlock', '<h2>')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isCommandActive('formatBlock') ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700'
            }`}
            title="Heading 2"
          >
            <span className="text-sm font-semibold">H2</span>
          </button>
          <button
            type="button"
            onClick={() => execCommand('formatBlock', '<h3>')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isCommandActive('formatBlock') ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700'
            }`}
            title="Heading 3"
          >
            <span className="text-xs font-semibold">H3</span>
          </button>
        </div>

        {/* Text Styles */}
        <div className="flex items-center gap-1 px-2 border-r border-gray-300">
          <button
            type="button"
            onClick={() => execCommand('bold')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isCommandActive('bold') ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700'
            }`}
            title="Bold"
          >
            <FiBold className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => execCommand('italic')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isCommandActive('italic') ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700'
            }`}
            title="Italic"
          >
            <FiItalic className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => execCommand('underline')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isCommandActive('underline') ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700'
            }`}
            title="Underline"
          >
            <FiUnderline className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => execCommand('strikeThrough')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isCommandActive('strikeThrough') ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700'
            }`}
            title="Strikethrough"
          >
            <FiMinus className="w-4 h-4" />
          </button>
        </div>

        {/* Alignment */}
        <div className="flex items-center gap-1 px-2 border-r border-gray-300">
          <button
            type="button"
            onClick={() => execCommand('justifyLeft')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isCommandActive('justifyLeft') ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700'
            }`}
            title="Align Left"
          >
            <FiAlignLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => execCommand('justifyCenter')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isCommandActive('justifyCenter') ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700'
            }`}
            title="Align Center"
          >
            <FiAlignCenter className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => execCommand('justifyRight')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isCommandActive('justifyRight') ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700'
            }`}
            title="Align Right"
          >
            <FiAlignRight className="w-4 h-4" />
          </button>
        </div>

        {/* Lists */}
        <div className="flex items-center gap-1 px-2 border-r border-gray-300">
          <button
            type="button"
            onClick={() => execCommand('insertUnorderedList')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isCommandActive('insertUnorderedList') ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700'
            }`}
            title="Bullet List"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              <circle cx="2" cy="6" r="1.5" fill="currentColor" />
              <circle cx="2" cy="12" r="1.5" fill="currentColor" />
              <circle cx="2" cy="18" r="1.5" fill="currentColor" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => execCommand('insertOrderedList')}
            className={`p-2 rounded hover:bg-gray-100 transition-colors ${
              isCommandActive('insertOrderedList') ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700'
            }`}
            title="Numbered List"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              <text x="2" y="8" fontSize="8" fill="currentColor" fontWeight="600">1</text>
              <text x="2" y="14" fontSize="8" fill="currentColor" fontWeight="600">2</text>
              <text x="2" y="20" fontSize="8" fill="currentColor" fontWeight="600">3</text>
            </svg>
          </button>
        </div>

        {/* Colors */}
        <div className="flex items-center gap-1 px-2 border-r border-gray-300">
          <input
            type="color"
            onInput={(e) => execCommand('foreColor', e.target.value)}
            className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
            title="Text Color"
          />
          <input
            type="color"
            onInput={(e) => execCommand('backColor', e.target.value)}
            className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
            title="Background Color"
          />
        </div>

        {/* Image */}
        <div className="flex items-center gap-1 px-2">
          <button
            type="button"
            onClick={handleImageButtonClick}
            className="p-2 rounded hover:bg-gray-100 text-gray-700 transition-colors"
            title="Insert Image"
          >
            <FiImage className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleContentChange}
        onBlur={handleContentChange}
        className="min-h-[100px] bg-gray-100 border border-gray-200 rounded-b-lg p-3 text-sm text-gray-700 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        style={{
          outline: 'none',
          minHeight: `${minHeight}px`,
        }}
        data-placeholder={placeholder}
      />

      {/* Image Modal */}
      {showImageModal && uploadedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-auto max-h-[90vh] flex flex-col">
            {/* Header - Fixed */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">Edit Image</h3>
              <button
                type="button"
                onClick={() => {
                  setShowImageModal(false);
                  setUploadedImage(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto flex-1 p-6">
              {/* Image Preview */}
              <div className="mb-4 flex justify-center">
                <div className="w-full" style={{ textAlign: imageAlign }}>
                  <img
                    src={uploadedImage}
                    alt="Preview"
                    style={{
                      maxWidth: imageSize.width === '100%' ? '100%' : imageSize.width.includes('%') 
                        ? `${Math.min(parseFloat(imageSize.width), 100)}%` 
                        : `${imageSize.width}px`,
                      height: imageSize.height === 'auto' ? 'auto' : `${imageSize.height}px`,
                      display: imageAlign === 'center' || imageAlign === 'right' ? 'block' : 'inline-block',
                      margin: imageAlign === 'center' ? '0 auto' : imageAlign === 'right' ? '0 0 0 auto' : '0',
                      maxHeight: '400px',
                      objectFit: 'contain',
                    }}
                    className="border border-gray-300 rounded"
                  />
                </div>
              </div>

              {/* Size Controls */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Width
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={imageSize.width}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Validate percentage values - don't allow above 100%
                        if (value.includes('%')) {
                          const numValue = parseFloat(value);
                          if (!isNaN(numValue) && numValue > 100) {
                            value = '100%';
                          }
                        }
                        setImageSize({ ...imageSize, width: value });
                      }}
                      placeholder="100% or 500px"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => setImageSize({ ...imageSize, width: '100%' })}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm transition-colors whitespace-nowrap"
                    >
                      Full Width
                    </button>
                  </div>
                  {imageSize.width.includes('%') && parseFloat(imageSize.width) > 100 && (
                    <p className="text-xs text-red-600 mt-1">Width cannot exceed 100%</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Height
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={imageSize.height}
                      onChange={(e) => setImageSize({ ...imageSize, height: e.target.value })}
                      placeholder="auto or 300px"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => setImageSize({ ...imageSize, height: 'auto' })}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm transition-colors whitespace-nowrap"
                    >
                      Auto
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alignment
                  </label>
                  <div className="flex gap-2">
                    {['left', 'center', 'right'].map((align) => (
                      <button
                        key={align}
                        type="button"
                        onClick={() => setImageAlign(align)}
                        className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                          imageAlign === align
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {align.charAt(0).toUpperCase() + align.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions - Fixed */}
            <div className="flex items-center justify-between p-6 pt-4 border-t border-gray-200 flex-shrink-0">
              <button
                type="button"
                onClick={deleteImageFromEditor}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
              >
                <FiTrash2 className="w-4 h-4" />
                <span>Delete Image</span>
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowImageModal(false);
                    setUploadedImage(null);
                  }}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => updateImageInEditor(imageSize.width, imageSize.height, imageAlign)}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                >
                  Apply Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .wysiwyg-editor-wrapper [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        .wysiwyg-editor-wrapper [contenteditable] img {
          max-width: 100%;
          height: auto;
          display: inline-block;
          cursor: pointer;
          transition: opacity 0.2s;
        }
        .wysiwyg-editor-wrapper [contenteditable] img:hover {
          opacity: 0.8;
        }
        .wysiwyg-editor-wrapper [contenteditable] a {
          color: #3b82f6;
          text-decoration: underline;
        }
        .wysiwyg-editor-wrapper [contenteditable] ul {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
          list-style-type: disc !important;
          list-style-position: outside !important;
        }
        .wysiwyg-editor-wrapper [contenteditable] ul li {
          margin: 0.25rem 0;
          padding-left: 0.5rem;
          list-style-type: disc !important;
          list-style-position: outside !important;
          display: list-item !important;
        }
        .wysiwyg-editor-wrapper [contenteditable] ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
          list-style-type: decimal !important;
          list-style-position: outside !important;
        }
        .wysiwyg-editor-wrapper [contenteditable] ol li {
          margin: 0.25rem 0;
          padding-left: 0.5rem;
          list-style-type: decimal !important;
          list-style-position: outside !important;
          display: list-item !important;
        }
        .wysiwyg-editor-wrapper [contenteditable] h1 {
          font-size: 1.875rem;
          font-weight: 700;
          margin: 0.5rem 0;
        }
        .wysiwyg-editor-wrapper [contenteditable] h2 {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 0.5rem 0;
        }
        .wysiwyg-editor-wrapper [contenteditable] h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0.5rem 0;
        }
      `}</style>
    </div>
  );
};

export default WysiwygEditor;

